import React, { useState } from 'react';
import TableContainer from 'Components/Common/TableContainerReactTable';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { formatPropertyType, formatStringWithUnderscores, getTruncatedId } from 'lib/utils/formatters';
import { setSelectedPropertyUnitId } from 'slices/builder/builderSlice';
import { AppDispatch } from 'slices/store';
import { useDispatch } from 'react-redux';

interface SoldListingTableProps {
    data: any[];
    isLoading?: boolean;
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    onRefresh?: () => void;
};

const SoldListingTable: React.FC<SoldListingTableProps> = ({
    data,
    currentPage = 1,
    totalPages = 1,
    totalCount = 0,
    onPageChange,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const customPageSize = 10;

     // Function to copy ID to clipboard
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 2 seconds
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const columns = [
        {
            header: 'No',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const index = (currentPage - 1) * customPageSize + row.index + 1;
                return <span>{index}</span>;
            },
        },
        {
            header: 'Property ID',
            accessorKey: 'id',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const fullId = getValue();
                if (!fullId) return 'N/A';
                
                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                            {getTruncatedId(fullId)}
                        </span>
                        <span
                            onClick={() => copyToClipboard(fullId)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: copiedId === fullId ? '#28a745' : '#6b7280',
                                transition: 'color 0.2s ease'
                            }}
                            title={copiedId === fullId ? 'Copied!' : 'Copy full ID'}
                        >
                            {copiedId === fullId ? (
                                <i className="ri-check-line"></i>
                            ) : (
                                <i className="ri-file-copy-line"></i>
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            header: 'Address',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                return row.original?.property?.property_address || 'N/A';
            },
        },
        {
            header: 'Property Type',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const raw = row.original?.property?.property_type;
                if (!raw) return 'N/A';
                return formatPropertyType(String(raw));
            }
        },
        {
            header: 'Sold To',
            accessorKey: 'sold_to',
            enableColumnFilter: false,
        },
        {
            header: 'Created At',
            accessorKey: 'createdAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Updated At',
            accessorKey: 'updatedAt',
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => {
                const date = getValue();
                if (!date) return 'N/A';
                const formattedDate = new Date(date).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                // Format date without comma
                const [day, month, year, time, ampm] = formattedDate.split(' ');
                return `${day} ${month} ${year} ${time} ${ampm}`;
            },
        },
        {
            header: 'Status',
            accessorKey: 'status',
            enableColumnFilter: false,
            cell: (cell: any) => {
                const raw = (cell.getValue() ?? '').toString();
                const formatted = formatStringWithUnderscores(raw);
                switch (raw.toLowerCase()) {
                    case "sold":
                        return (<span className="badge rounded-pill bg-success"> {formatted}</span>);
                    default:
                        return (<span className="badge rounded-pill  bg-warning"> {formatted}</span>);
                }
            },
        },
        {
            header: 'Action',
            accessorKey: 'action',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => (
                <UncontrolledDropdown direction="start">
                    <DropdownToggle tag="span" style={{ cursor: 'pointer' }}>
                        <i className="ri-more-2-fill" style={{ fontSize: 20, color: '#6b7280' }}></i>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem
                            onClick={() => {
                                const selectedPropertyUnitId = row?.original?.id;
                                dispatch(setSelectedPropertyUnitId(selectedPropertyUnitId));
                                
                                // Pre-set localStorage before navigation
                                localStorage.setItem('activeMenu', 'builder');
                                
                                navigate('/listing-detail?source=builder');
                            }}
                            className="d-flex align-items-center"
                        >
                            <i className="ri-eye-line me-2"></i> View
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            ),
        },
    ];
    return (
        <React.Fragment>
            <TableContainer
                columns={columns}
                data={data}
                customPageSize={customPageSize}
                tableClass="table-centered align-middle table-nowrap mb-0"
                theadClass="text-muted table-light"
                isServerSide={true}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={onPageChange}
            />
        </React.Fragment>
    )
}

export default SoldListingTable;